import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { SkeletonLoader, colors2 } from "oolib";
import { UI_BODY_SM, UI_BODY_SEMIBOLD_SM_DF, UI_BODY_SEMIBOLD_DF, UI_TAG, UI_CAPTION_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";
import { UsersThree } from "@phosphor-icons/react";
import { DashboardWidgetShell } from "../../comps/DashboardWidgetShell";
import { PillDropdown } from "../../comps/PillDropdown";
import { StatusPill } from "../comps/StatusPill";
import { sentimentPillTheme } from "../sentiment";
import { useLensesV2Context } from "../LensesV2Context";
import { useAggrMetricsAcrossDimensions } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import { buildRollupDimension, assembleSegments } from "./utils";
import { CONFIG } from "./CONFIG";

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  flex-wrap: wrap;
  padding: 0 0 1rem;
`;

const Control = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${colors2.grey50};
`;

const SegList = styled.div`
  display: flex;
  flex-direction: column;
`;

const SegRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1.2rem 0;
  border-bottom: 1px solid ${colors2.grey5};
  &:last-child {
    border-bottom: none;
  }
`;

const SegRowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const ChipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
  background: ${colors2.grey5};
  color: ${colors2.grey60};
`;

const SummaryPlaceholder = styled(UI_BODY_SM)`
  color: ${colors2.grey30};
  font-style: italic;
`;

// User Segments widget — per demographic segment, a breakdown by the chosen
// attribute lens (Signals or Sentiment) + a per-segment summary. Segments come from
// ONE aggrMetrics call grouping annotations by a demographic rollup dimension ×
// their attribute tags (see utils). Sentiment reuses the shared SENTIMENT_SOURCE.
// Summary is a placeholder for now.
export const SegmentWidget = () => {
  const { demographics, findQuery, signals, emotionAffect } = useLensesV2Context();

  const linkedDataset = demographics?.linkedDataset;
  const dimensions = useMemo(
    () => (demographics?.dimensions || []).filter((d) => d.kind === "categorical" || d.kind === "tagRef"),
    [demographics]
  );

  // selected demographic dimension
  const [selectedPath, setSelectedPath] = useState(null);
  useEffect(() => {
    if (!selectedPath && dimensions.length) setSelectedPath(dimensions[0].valuePath);
  }, [dimensions, selectedPath]);
  const selectedDim = dimensions.find((d) => d.valuePath === selectedPath) || null;

  // attribute lens (signals | sentiment)
  const [attribute, setAttribute] = useState(CONFIG.defaultAttribute);

  const rollupDimension = useMemo(() => buildRollupDimension(linkedDataset, selectedDim), [linkedDataset, selectedDim]);

  const breakdown = useAggrMetricsAcrossDimensions({
    contentType: "annotations",
    dimensions: rollupDimension ? [rollupDimension, "annotations.tags.tags"] : [],
    findQuery,
    vizCardId: "lensV2SegmentBreakdown",
    queryOptions: { enabled: !!findQuery && !!rollupDimension },
  });

  const segments = useMemo(
    () => assembleSegments(breakdown.data, { signals, emotionAffect }, CONFIG.sentimentSource),
    [breakdown.data, signals, emotionAffect]
  );

  const dimOptions = dimensions.map((d) => ({ value: d.valuePath, display: d.label }));

  return (
    <DashboardWidgetShell
      titleJSX={
        <TitleWrapper>
          <UsersThree size={16} color={colors2.grey80} weight="bold" />
          <UI_BODY_SEMIBOLD_SM_DF>User Segments</UI_BODY_SEMIBOLD_SM_DF>
        </TitleWrapper>
      }
      style={{ gap: 0 }}
      bodyStyle={{ overflowY: "auto", overflowX: "hidden", maxHeight: 560 }}
    >
      {!linkedDataset ? (
        <UI_BODY_SM style={{ color: colors2.grey40 }}>
          No linked demographic dataset found for this tenant.
        </UI_BODY_SM>
      ) : (
        <>
          <Controls>
            <Control>
              <UI_CAPTION_SEMIBOLD_DF>Segment by</UI_CAPTION_SEMIBOLD_DF>
              <PillDropdown
                value={selectedDim ? { value: selectedDim.valuePath, display: selectedDim.label } : null}
                onChange={(id, val) => setSelectedPath(val?.value || null)}
                options={dimOptions}
              />
            </Control>
            <Control>
              <UI_CAPTION_SEMIBOLD_DF>Show</UI_CAPTION_SEMIBOLD_DF>
              <PillDropdown
                value={CONFIG.attributes.find((a) => a.value === attribute)}
                onChange={(id, val) => setAttribute(val?.value || CONFIG.defaultAttribute)}
                options={CONFIG.attributes}
              />
            </Control>
          </Controls>

          {breakdown.isLoading && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} style={{ padding: "8px 0" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <SkeletonLoader style={{ height: 14, width: `${35 + i * 8}%`, borderRadius: 4 }} />
                    <SkeletonLoader style={{ height: 14, width: 60, borderRadius: 4 }} />
                  </div>
                  <SkeletonLoader style={{ height: 22, width: "60%", borderRadius: 12, marginTop: 8 }} />
                </div>
              ))}
            </div>
          )}

          {breakdown.isError && (
            <UI_BODY_SM style={{ color: "#dc2626" }}>
              Error: {breakdown.error?.response?.data?.message || breakdown.error?.message}
            </UI_BODY_SM>
          )}

          {breakdown.data && segments.length === 0 && (
            <UI_BODY_SM style={{ color: colors2.grey40 }}>No segment data available yet.</UI_BODY_SM>
          )}

          {breakdown.data && segments.length > 0 && (
            <SegList>
              {segments.map((seg) => (
                <SegRow key={seg.segmentValue}>
                  <SegRowTop>
                    <UI_BODY_SEMIBOLD_DF style={{ color: colors2.grey90 }}>{seg.segment}</UI_BODY_SEMIBOLD_DF>
                    <UI_BODY_SM style={{ color: colors2.grey40, flexShrink: 0 }}>{seg.total} mentions</UI_BODY_SM>
                  </SegRowTop>

                  {attribute === "signals" ? (
                    seg.signals.length > 0 ? (
                      <ChipRow>
                        {seg.signals.map((s) => (
                          <Chip key={s.signalId}>
                            <UI_TAG>
                              {s.signal} · {s.count}
                            </UI_TAG>
                          </Chip>
                        ))}
                      </ChipRow>
                    ) : (
                      <UI_BODY_SM style={{ color: colors2.grey40 }}>No signals</UI_BODY_SM>
                    )
                  ) : (
                    <div>
                      <StatusPill theme={sentimentPillTheme(seg.sentiment)} />
                    </div>
                  )}

                  <SummaryPlaceholder>Summary coming soon…</SummaryPlaceholder>
                </SegRow>
              ))}
            </SegList>
          )}
        </>
      )}
    </DashboardWidgetShell>
  );
};
